import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon, type IconName } from '@capsico/ui';
import { Toasts } from './ui';

/*
 * Customer shell: one 480 px column, content above a fixed bottom nav on the three tab roots
 * (Home · Search · Account). Sub-pages (restaurant, address, profile, sign-in) hide the nav.
 * Active tab = ink label with a short yellow bar; inactive = ink-3.
 */

export interface NavTab {
  to: string;
  label: string;
  icon: IconName;
}

export function Shell({
  tabs,
  showNav,
  raised = false,
  children,
}: {
  tabs: NavTab[];
  showNav: boolean;
  /** A floating bar (the cart bar) sits above the nav: leave room so it never hides content. */
  raised?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto min-h-dvh w-full max-w-120 bg-surface">
      <div className={showNav ? (raised ? 'pb-36' : 'pb-20') : ''}>{children}</div>
      {showNav && (
        <nav
          aria-label="Main"
          className="fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-120 bg-surface pb-[env(safe-area-inset-bottom)] shadow-[inset_0_1px_0_var(--color-hair),0_-4px_12px_rgba(15,23,42,0.06)]"
        >
          <div
            className="grid"
            style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
          >
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === '/'}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2 transition-colors ${isActive ? 'text-ink' : 'text-ink-3'}`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon name={tab.icon} size={22} />
                    <span
                      className={`mt-0.5 text-caption ${isActive ? 'font-semibold' : 'font-medium'}`}
                    >
                      {tab.label}
                    </span>
                    <span
                      className={`mt-1 h-0.5 w-6 rounded-full ${isActive ? 'bg-brand' : 'bg-transparent'}`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
      <Toasts />
    </div>
  );
}
